import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { fetchCsrfToken, submitContactMessage } from '../../api/contact';
import './Contact.css';

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'services', label: 'Service Information' },
  { value: 'tech_development', label: 'Technology Development' },
  { value: 'business_consulting', label: 'Business Consulting' },
  { value: 'partnership', label: 'Partnership Opportunity' },
  { value: 'support', label: 'Support Request' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'other', label: 'Other' },
];

const initialFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company_name: '',
  inquiry_type: 'general',
  subject: '',
  message: '',
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchCsrfToken().catch(console.error);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitContactMessage(formData);
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setFormData(initialFormData);
    setSubmitSuccess(false);
    setSubmitError(null);
    setErrors({});
  };

  if (submitSuccess) {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <div className="text-success mb-3">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <h3 className="mb-3">Message Sent Successfully!</h3>
                <p className="text-muted mb-4">
                  Thank you for contacting us. We will review your message and get back to you as soon as possible.
                </p>
                <Button variant="primary" onClick={handleSendAnother}>
                  Send Another Message
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Helmet>
        <title>Contact United Arborist | Technology, Consulting & Tree Care Inquiries</title>
        <meta name="description" content="Contact United Arborist for technology development, business consulting, subcontractor staffing, or tree care industry inquiries. Get a response within one business day." />
        <meta name="keywords" content="contact United Arborist, arborist consulting inquiry, tree service technology development, tree care business consulting, tree service contact, hire tree subcontractors, arborist staffing inquiry" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.unitedarborist.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="United Arborist" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Contact United Arborist | Technology, Consulting & Tree Care Inquiries" />
        <meta property="og:description" content="Contact United Arborist for technology development, business consulting, or subcontractor connections in the tree care industry." />
        <meta property="og:url" content="https://www.unitedarborist.com/contact" />
        <meta property="og:image" content="https://www.unitedarborist.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact United Arborist | Technology, Consulting & Tree Care Inquiries" />
        <meta name="twitter:description" content="Contact United Arborist for technology development, business consulting, or subcontractor connections in the tree care industry." />
        <meta name="twitter:image" content="https://www.unitedarborist.com/logo.jpg" />
      </Helmet>
      <Row className="justify-content-center">
        <Col lg={8}>
          <h2 className="text-center mb-4">Contact Us</h2>
          <Card className="shadow-sm">
            <Card.Body>

                  {submitError && (
                    <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
                      {submitError}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            isInvalid={!!errors.first_name}
                            placeholder="Enter your first name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.first_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            isInvalid={!!errors.last_name}
                            placeholder="Enter your last name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.last_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            placeholder="Enter your email"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Enter your phone number"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            placeholder="Enter your company name (optional)"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Inquiry Type</Form.Label>
                          <Form.Select
                            name="inquiry_type"
                            value={formData.inquiry_type}
                            onChange={handleChange}
                          >
                            {INQUIRY_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        isInvalid={!!errors.subject}
                        placeholder="Enter the subject of your message"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.subject}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Message <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        isInvalid={!!errors.message}
                        placeholder="Enter your message"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-grid d-md-block">
                      <Button
                        variant="success"
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="px-5"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>
                  </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Contact;
